import { useState, useEffect } from 'react'
import { ArrowLeft, CheckCircle2, FileText, Loader2, Plus, User } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { DJENPublication } from '@/types/publication'
import { caseService } from '@/services/case-service'
import { PublicationService } from '@/services/publicationService'
import { CreateCaseFromPublicationForm } from './CreateCaseFromPublicationForm'
import { toast } from 'sonner'

interface LinkCaseModalProps {
  isOpen: boolean
  onClose: () => void
  publication: DJENPublication
  onLinked: (caseId: string) => void | Promise<void>
}

type ModalMode = 'searching' | 'select' | 'create'

interface CaseSearchResult {
  id: string
  caseNumber: string
  internalNumber: string
  title: string
  status: string
  clientName: string
}

export function LinkCaseModal({ isOpen, onClose, publication, onLinked }: LinkCaseModalProps) {
  const [mode, setMode] = useState<ModalMode>('searching')
  const [cases, setCases] = useState<CaseSearchResult[]>([])
  const [linking, setLinking] = useState(false)

  useEffect(() => {
    if (isOpen) {
      searchExistingCases()
    }
    // searchExistingCases referencia publication.numeroProcesso/id; rodamos só ao abrir o modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const searchExistingCases = async () => {
    try {
      setMode('searching')

      const result = await caseService.findByProcessNumber(publication.numeroProcesso)

      if (result.found && result.cases.length > 0) {
        setCases(result.cases)

        // ✨ Auto-link if only 1 case found
        if (result.cases.length === 1) {
          await handleLinkToCase(result.cases[0].id)
        } else {
          setMode('select')
        }
      } else {
        setCases([])
        setMode('select') // Still show select mode with "create new" option
      }
    } catch (error) {
      console.error('Error searching cases:', error)
      toast.error('Erro ao buscar processos existentes')
      setMode('select')
    }
  }

  const handleLinkToCase = async (caseId: string) => {
    try {
      setLinking(true)

      await PublicationService.linkToCase(publication.id, caseId)

      toast.success('Publicação vinculada ao processo com sucesso!')
      await onLinked(caseId)
    } catch (error) {
      console.error('Error linking publication:', error)
      toast.error('Erro ao vincular publicação ao processo')
    } finally {
      setLinking(false)
    }
  }

  const handleCaseCreated = async (caseId: string) => {
    try {
      // Link the publication to the newly created case
      await PublicationService.linkToCase(publication.id, caseId)

      toast.success('Processo criado e vinculado com sucesso!')
      await onLinked(caseId)
    } catch (error) {
      console.error('Error linking publication to new case:', error)
      toast.error('Processo criado, mas erro ao vincular publicação')
    }
  }

  const renderContent = () => {
    if (mode === 'searching') {
      return (
        <div role="status" className="flex flex-col items-center justify-center py-12 text-center">
          <Loader2 className="mb-4 size-8 animate-spin text-muted-foreground" aria-hidden="true" />
          <p className="text-muted-foreground">Buscando processos existentes...</p>
          <p className="mt-2 break-all text-sm text-muted-foreground">
            Processo: {publication.numeroProcessoComMascara}
          </p>
        </div>
      )
    }

    if (mode === 'select') {
      return (
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border bg-muted/40 p-3">
            <h4 className="mb-2 font-medium">Publicação</h4>
            <div className="flex flex-col gap-1 break-words text-sm text-muted-foreground">
              <p><strong>Tipo:</strong> {publication.tipoComunicacao}</p>
              <p><strong>Processo:</strong> {publication.numeroProcessoComMascara}</p>
              <p><strong>Tribunal:</strong> {publication.siglaTribunal}</p>
            </div>
          </div>

          {cases.length > 0 ? (
            <>
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <CheckCircle2 className="size-5 shrink-0 text-primary" aria-hidden="true" />
                  <h4 className="font-semibold">
                    Encontramos {cases.length} processo{cases.length > 1 ? 's' : ''} com este número
                  </h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Selecione um processo existente ou cadastre um novo
                </p>
              </div>

              <div className="flex max-h-96 flex-col gap-3 overflow-y-auto">
                {cases.map((case_) => (
                  <div
                    key={case_.id}
                    className="rounded-lg border p-3 transition-colors hover:bg-muted/50"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="mb-2 flex items-center gap-2">
                          <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                          <h5 className="font-medium wrap-break-word">{case_.title}</h5>
                        </div>
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <p className="wrap-break-word">
                            <strong>Nº Interno:</strong> {case_.internalNumber} | <strong>Status:</strong> {case_.status}
                          </p>
                          <p className="flex items-center gap-1 wrap-break-word">
                            <User className="size-3 shrink-0" aria-hidden="true" />
                            <strong>Cliente:</strong> {case_.clientName}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleLinkToCase(case_.id)}
                        disabled={linking}
                        className="shrink-0 self-start"
                      >
                        {linking ? 'Vinculando...' : 'Usar Este Processo'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => setMode('create')}
                  className="w-full"
                >
                  <Plus className="size-4" aria-hidden="true" />
                  Cadastrar Novo Processo
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 py-6 text-center">
              <FileText className="size-8 text-muted-foreground" aria-hidden="true" />
              <h4 className="font-medium">Nenhum processo encontrado</h4>
              <p className="mb-4 break-words text-sm text-muted-foreground">
                Não encontramos processos com o número {publication.numeroProcessoComMascara}
              </p>
              <Button
                onClick={() => setMode('create')}
                className="w-full"
              >
                <Plus className="size-4" aria-hidden="true" />
                Cadastrar Novo Processo
              </Button>
            </div>
          )}
        </div>
      )
    }

    if (mode === 'create') {
      return (
        <div>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMode('select')}
              className="-ml-2"
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
              Voltar
            </Button>
            <h3 className="text-lg font-semibold">
              Cadastrar Novo Processo
            </h3>
          </div>

          <CreateCaseFromPublicationForm
            publication={publication}
            onSuccess={handleCaseCreated}
            onCancel={() => setMode('select')}
          />
        </div>
      )
    }
  }

  const getTitle = () => {
    if (mode === 'searching') return 'Buscando Processos...'
    if (mode === 'select') return 'Vincular Publicação ao Processo'
    if (mode === 'create') return 'Cadastrar Novo Processo'
    return 'Vincular Publicação'
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent size="lg" fullScreenOnMobile className="max-h-[90vh] overflow-y-auto max-sm:max-h-dvh">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
        </DialogHeader>
        <div>
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  )
}
